/**
 * Payment Statement request screen.
 *
 * The nurse picks a month + year and taps Generate; the API renders the PDF and
 * emails it to her. Nothing is downloaded to the device — the statement can
 * carry several pages of visit lines, and email is where she'll want it anyway
 * (forwarding it to an accountant is the main use).
 *
 * These are contractor payment statements, not pay stubs — the copy here must
 * not imply payroll, wages, or proof of employment.
 */
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import { useMemo, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { requestPaymentStatement, type PaymentStatementResult } from "@/lib/api";
import { colors, fonts, gradients, radius, shadows, spacing } from "@/lib/theme";

const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];

/** Task Health has no pay ledger before this, so there is nothing to state. */
const EARLIEST_YEAR = 2025;

export default function PaymentStatementScreen() {
    const router = useRouter();
    const now = useMemo(() => new Date(), []);

    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sent, setSent] = useState<PaymentStatementResult | null>(null);

    const years = useMemo(() => {
        const out: number[] = [];
        for (let y = now.getFullYear(); y >= EARLIEST_YEAR; y--) {
            out.push(y);
        }
        return out;
    }, [now]);

    /** A month that hasn't started yet has nothing to report. */
    function isFuture(y: number, m: number): boolean {
        return y > now.getFullYear() || (y === now.getFullYear() && m > now.getMonth() + 1);
    }

    function selectYear(y: number) {
        setYear(y);
        setSent(null);
        setError(null);
        // Keep the selection legal when jumping to the current year.
        if (isFuture(y, month)) {
            setMonth(now.getMonth() + 1);
        }
    }

    function selectMonth(m: number) {
        if (isFuture(year, m)) {
            return;
        }
        setMonth(m);
        setSent(null);
        setError(null);
    }

    async function handleGenerate() {
        if (busy) {
            return;
        }
        setBusy(true);
        setError(null);
        setSent(null);
        try {
            const res = await requestPaymentStatement(year, month);
            setSent(res);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Couldn't generate your statement");
        } finally {
            setBusy(false);
        }
    }

    return (
        <SafeAreaView style={styles.container} edges={["top"]}>
            <View style={styles.header}>
                <TouchableOpacity onPress={() => router.back()} hitSlop={12}>
                    <MaterialCommunityIcons
                        name="arrow-left"
                        size={22}
                        color={colors.textPrimary}
                    />
                </TouchableOpacity>
                <Text style={styles.title}>Payment statement</Text>
                <View style={{ width: 22 }} />
            </View>

            <ScrollView contentContainerStyle={styles.content}>
                <Text style={styles.intro}>
                    Pick a month and we'll email you a PDF statement of the payments you received
                    for that period.
                </Text>

                <Text style={styles.label}>Year</Text>
                <View style={styles.yearRow}>
                    {years.map((y) => {
                        const active = y === year;
                        return (
                            <TouchableOpacity
                                key={y}
                                onPress={() => selectYear(y)}
                                style={[styles.yearChip, active && styles.yearChipActive]}
                                activeOpacity={0.8}
                            >
                                <Text style={[styles.yearText, active && styles.yearTextActive]}>
                                    {y}
                                </Text>
                            </TouchableOpacity>
                        );
                    })}
                </View>

                <Text style={styles.label}>Month</Text>
                <View style={styles.monthGrid}>
                    {MONTHS.map((name, i) => {
                        const m = i + 1;
                        const disabled = isFuture(year, m);
                        const active = m === month && !disabled;
                        return (
                            <TouchableOpacity
                                key={name}
                                onPress={() => selectMonth(m)}
                                disabled={disabled}
                                activeOpacity={0.8}
                                style={[
                                    styles.monthCell,
                                    active && styles.monthCellActive,
                                    disabled && styles.monthCellDisabled
                                ]}
                            >
                                <Text
                                    style={[
                                        styles.monthText,
                                        active && styles.monthTextActive,
                                        disabled && styles.monthTextDisabled
                                    ]}
                                >
                                    {name.slice(0, 3)}
                                </Text>
                            </TouchableOpacity>
                        );
                    })}
                </View>

                {error ? (
                    <View style={styles.errorBox}>
                        <MaterialCommunityIcons
                            name="alert-circle-outline"
                            size={18}
                            color={colors.error500}
                        />
                        <Text style={styles.errorText}>{error}</Text>
                    </View>
                ) : null}

                {sent ? (
                    <View style={styles.successBox}>
                        <MaterialCommunityIcons
                            name="email-check-outline"
                            size={22}
                            color={colors.success600}
                        />
                        <View style={styles.successTextCol}>
                            <Text style={styles.successTitle}>Sent to {sent.email}</Text>
                            <Text style={styles.successBody}>
                                Your {sent.periodLabel} statement is on its way as a PDF attachment.
                                Check your email — it can take a minute to arrive, and it may land
                                in spam.
                            </Text>
                        </View>
                    </View>
                ) : null}

                <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={handleGenerate}
                    disabled={busy}
                    style={[styles.ctaOuter, busy && styles.ctaDisabled]}
                >
                    <LinearGradient
                        colors={gradients.brand}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={styles.cta}
                    >
                        {busy ? (
                            <ActivityIndicator color={colors.surface} />
                        ) : (
                            <Text style={styles.ctaText}>
                                Generate {MONTHS[month - 1]} {year}
                            </Text>
                        )}
                    </LinearGradient>
                </TouchableOpacity>

                {busy ? (
                    <Text style={styles.busyNote}>
                        Building your statement, this can take a few seconds…
                    </Text>
                ) : null}

                <Text style={styles.disclaimer}>
                    A payment statement is a record of the fees paid to you for services rendered as
                    an independent contractor. It is not a pay stub or proof of employment.
                </Text>
            </ScrollView>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    header: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.md,
        paddingBottom: spacing.md
    },
    title: { fontSize: 17, fontFamily: fonts.bold, color: colors.textPrimary },
    content: { paddingHorizontal: spacing.lg, paddingBottom: spacing["3xl"] },

    intro: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        marginBottom: spacing.xl,
        lineHeight: 20
    },
    label: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.textTertiary,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        marginBottom: spacing.sm
    },

    yearRow: { flexDirection: "row", flexWrap: "wrap", gap: spacing.sm, marginBottom: spacing.xl },
    yearChip: {
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.sm,
        borderRadius: radius.full,
        borderWidth: 1.5,
        borderColor: colors.border,
        backgroundColor: colors.surface
    },
    yearChipActive: { borderColor: colors.teal400, backgroundColor: colors.teal50 },
    yearText: { fontSize: 14, fontFamily: fonts.semiBold, color: colors.textSecondary },
    yearTextActive: { color: colors.teal600 },

    monthGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: spacing.sm,
        marginBottom: spacing.xl
    },
    monthCell: {
        width: "22%",
        paddingVertical: spacing.md,
        borderRadius: radius.lg,
        borderWidth: 1.5,
        borderColor: colors.border,
        backgroundColor: colors.surface,
        alignItems: "center"
    },
    monthCellActive: { borderColor: colors.teal400, backgroundColor: colors.teal50 },
    monthCellDisabled: { opacity: 0.35 },
    monthText: { fontSize: 13, fontFamily: fonts.semiBold, color: colors.textSecondary },
    monthTextActive: { color: colors.teal600 },
    monthTextDisabled: { color: colors.textTertiary },

    errorBox: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.sm,
        backgroundColor: colors.error50,
        borderRadius: radius.lg,
        padding: spacing.base,
        marginBottom: spacing.base
    },
    errorText: {
        flex: 1,
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.error600,
        lineHeight: 19
    },

    successBox: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.md,
        backgroundColor: colors.success50,
        borderRadius: radius.lg,
        padding: spacing.base,
        marginBottom: spacing.base
    },
    successTextCol: { flex: 1, gap: 3 },
    successTitle: { fontSize: 14, fontFamily: fonts.bold, color: colors.success600 },
    successBody: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        lineHeight: 19
    },

    ctaOuter: { borderRadius: radius.full, marginTop: spacing.sm, ...shadows.brand },
    ctaDisabled: { opacity: 0.7 },
    cta: {
        borderRadius: radius.full,
        paddingVertical: spacing.base,
        alignItems: "center",
        justifyContent: "center",
        minHeight: 52
    },
    ctaText: { fontSize: 16, fontFamily: fonts.bold, color: colors.surface },
    busyNote: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textTertiary,
        textAlign: "center",
        marginTop: spacing.sm
    },

    disclaimer: {
        fontSize: 11,
        fontFamily: fonts.medium,
        color: colors.textTertiary,
        lineHeight: 17,
        marginTop: spacing.xl
    }
});
